import { AlertModal, ButtonType } from '@ui-components/containers/AlertModal'
import { ModalVisualMode } from '@ui-components/containers/ModalTypes'
import { NoButtonsModal } from '@ui-components/containers/NoButtonsModal'
import { PromptModal } from '@ui-components/containers/PromptModal'
import { useSelector } from '@xstate/react'
import { JSX, ReactNode } from 'react'
import { ActorRefFrom } from 'xstate'

import { modalMachine, OPEN } from './modalMachine'

export interface PrimaryButtonOptions {
  label: string
  onClick?: () => unknown
  type?: ButtonType
}

export interface SecondaryButtonOptions {
  label: string
  onClick?: () => unknown
}

/**
 * @param contentAriaLabel - custom aria label for content. Defaults to header.
 */
export interface ShowModalOptions {
  children: ReactNode
  contentAriaLabel?: string
  header?: string
  headerAdditionalText?: string
  primaryButtonOptions?: PrimaryButtonOptions
  secondaryButtonOptions?: SecondaryButtonOptions
  mode?: ModalVisualMode
  requestId?: string
}

export interface ModalProps {
  service: ActorRefFrom<typeof modalMachine>
}

const defaultOptions: ShowModalOptions = {
  mode: ModalVisualMode.Plain,
  children: undefined,
  header: '',
}

export const Modal = ({ service }: ModalProps): JSX.Element => {
  const state = useSelector(service, (s) => s)

  const context = state.context
  const options = { ...defaultOptions, ...context.options }
  const {
    primaryButtonOptions,
    secondaryButtonOptions,
    mode,
    contentAriaLabel,
    children,
    header,
    headerAdditionalText,
    requestId,
  } = options

  const onPrimaryButtonClick = () => {
    service.send({ type: 'CONFIRM' })
    if (primaryButtonOptions?.onClick) {
      primaryButtonOptions.onClick()
    }
  }

  const onSecondaryButtonClick = () => {
    onClose()
    if (secondaryButtonOptions?.onClick) {
      secondaryButtonOptions.onClick()
    }
  }

  const onClose = () => {
    service.send({ type: 'DISMISS' })
  }

  const primaryButtonLabel = primaryButtonOptions?.label ?? ''
  const secondaryButtonLabel = secondaryButtonOptions?.label ?? ''

  const shouldShowPromptModal = state.matches(OPEN) && !!secondaryButtonOptions
  const shouldShowAlertModal = state.matches(OPEN) && !shouldShowPromptModal && !!primaryButtonOptions
  const shouldShowNoButtonsModal = state.matches(OPEN) && !secondaryButtonOptions && !primaryButtonOptions
  return (
    <>
      <AlertModal
        contentLabel={contentAriaLabel}
        onButtonClick={onPrimaryButtonClick}
        buttonLabel={primaryButtonLabel}
        buttonType={primaryButtonOptions?.type}
        isOpen={shouldShowAlertModal}
        header={header}
        mode={mode}
        requestId={requestId}
      >
        {children}
      </AlertModal>
      <PromptModal
        contentLabel={contentAriaLabel}
        onPrimaryButtonClick={onPrimaryButtonClick}
        onSecondaryButtonClick={onSecondaryButtonClick}
        onClose={onClose}
        primaryButtonLabel={primaryButtonLabel}
        secondaryButtonLabel={secondaryButtonLabel}
        isOpen={shouldShowPromptModal}
        header={header}
        headerAdditionalText={headerAdditionalText}
        mode={mode}
        requestId={requestId}
      >
        {children}
      </PromptModal>
      <NoButtonsModal
        mode={mode}
        contentLabel={contentAriaLabel}
        onClose={onClose}
        isOpen={shouldShowNoButtonsModal}
        header={header}
        headerAdditionalText={headerAdditionalText}
        requestId={requestId}
      >
        {children}
      </NoButtonsModal>
    </>
  )
}
